import React, { useEffect, useMemo, useState, lazy } from 'react';
import { Search } from 'lucide-react';
type Category = 'All Integrations' | 'Accounting' | 'Ads & Conversion' | 'AI Agents' | 'AI Meeting Assistants' | 'AI Models' | 'AI Web Scraping' | 'Analytics' | 'Artificial Intelligence' | 'Bookmark Managers' | 'Calendar' | 'CMS' | 'Communication' | 'Contact Management' | 'CRM' | 'Customer Support' | 'Databases' | 'Design' | 'Developer Tools' | 'Documents' | 'E-commerce' | 'Education' | 'Email' | 'Email Newsletters' | 'Event Management' | 'File Management & Storage' | 'Fitness' | 'Forms & Surveys' | 'Fundraising' | 'Images & Design' | 'IT Operations' | 'Lifestyle & Entertainment' | 'Marketing' | 'Marketing Automation' | 'Notes' | 'Notifications' | 'Payment Processing' | 'Phone & SMS' | 'Product Management' | 'Productivity' | 'Project Management' | 'Proposal & Invoice Management' | 'Sales' | 'Scheduling & Booking' | 'Security' | 'Server Monitoring' | 'Signatures' | 'Social Media Accounts' | 'Social Media Marketing' | 'Spreadsheets' | 'Task Management' | 'Team Chat' | 'Team Collaboration' | 'Time Tracking Software' | 'Transcription' | 'URL Shortener' | 'Video & Audio' | 'Video Conferencing' | 'Website Builders' | 'CI/CD & DevOps' | 'Cloud Infrastructure' | 'Data Warehouses' | 'ETL & Data Pipelines' | 'Healthcare' | 'HR & Recruiting' | 'Knowledge Base' | 'Legal & Contracts' | 'Logistics & Shipping' | 'Observability & Monitoring' | 'Password & Identity' | 'Real Estate';
type Integration = {
  slug: string;
  name: string;
  category: Category;
  // `light` flags brands whose mark is near-black; we render those white so
  // they read on the dark stage.
  light?: boolean;
  // `domain` is the company's primary web domain. Many newer brands (Ramp,
  // Brex, CrewAI, Fireflies, Granola, Firecrawl, Bright Data, etc.) have no
  // Simple Icons / dashboard-icons entry, so the icon CDNs return nothing and
  // the tile renders blank. When `domain` is set we load the logo from a
  // domain-based logo service first (which covers virtually every real
  // company), and fall back to the icon CDNs only if that fails.
  domain?: string;
};
// Comprehensive directory — the most-used tools across every category. Slugs
// are simpleicons identifiers (with a dashboard-icons fallback at render time,
// and a graceful hide if neither resolves).
const INTEGRATIONS: Integration[] = [
// ── Accounting ─────────────────────────────────────────────────────────
{
  slug: 'quickbooks',
  name: 'QuickBooks',
  category: 'Accounting',
  domain: 'quickbooks.intuit.com'
}, {
  slug: 'xero',
  name: 'Xero',
  category: 'Accounting',
  domain: 'xero.com'
}, {
  slug: 'freshbooks',
  name: 'FreshBooks',
  category: 'Accounting',
  domain: 'freshbooks.com'
}, {
  slug: 'wave',
  name: 'Wave',
  category: 'Accounting',
  domain: 'waveapps.com'
}, {
  slug: 'sage',
  name: 'Sage',
  category: 'Accounting',
  domain: 'sage.com'
}, {
  slug: 'zoho',
  name: 'Zoho Books',
  category: 'Accounting',
  domain: 'zoho.com'
}, {
  slug: 'expensify',
  name: 'Expensify',
  category: 'Accounting',
  domain: 'expensify.com'
}, {
  slug: 'ramp',
  name: 'Ramp',
  category: 'Accounting',
  domain: 'ramp.com'
}, {
  slug: 'brex',
  name: 'Brex',
  category: 'Accounting',
  domain: 'brex.com'
},
// ── Ads & Conversion ───────────────────────────────────────────────────
{
  slug: 'googleads',
  name: 'Google Ads',
  category: 'Ads & Conversion',
  domain: 'ads.google.com'
}, {
  slug: 'meta',
  name: 'Meta Ads',
  category: 'Ads & Conversion',
  domain: 'facebook.com'
}, {
  slug: 'tiktok',
  name: 'TikTok Ads',
  category: 'Ads & Conversion',
  domain: 'tiktok.com'
}, {
  slug: 'linkedin',
  name: 'LinkedIn Ads',
  category: 'Ads & Conversion',
  domain: 'linkedin.com'
}, {
  slug: 'reddit',
  name: 'Reddit Ads',
  category: 'Ads & Conversion',
  domain: 'reddit.com'
}, {
  slug: 'pinterest',
  name: 'Pinterest Ads',
  category: 'Ads & Conversion',
  domain: 'pinterest.com'
}, {
  slug: 'snapchat',
  name: 'Snapchat Ads',
  category: 'Ads & Conversion',
  domain: 'snapchat.com'
}, {
  slug: 'amazon',
  name: 'Amazon Ads',
  category: 'Ads & Conversion',
  domain: 'advertising.amazon.com'
}, {
  slug: 'googletagmanager',
  name: 'Tag Manager',
  category: 'Ads & Conversion',
  domain: 'tagmanager.google.com'
},
// ── AI Agents ──────────────────────────────────────────────────────────
{
  slug: 'langchain',
  name: 'LangChain',
  category: 'AI Agents',
  domain: 'langchain.com'
}, {
  slug: 'n8n',
  name: 'n8n',
  category: 'AI Agents',
  domain: 'n8n.io'
}, {
  slug: 'crewai',
  name: 'CrewAI',
  category: 'AI Agents',
  domain: 'crewai.com'
}, {
  slug: 'llamaindex',
  name: 'LlamaIndex',
  category: 'AI Agents',
  domain: 'llamaindex.ai'
}, {
  slug: 'relevanceai',
  name: 'Relevance AI',
  category: 'AI Agents',
  domain: 'relevanceai.com'
},
// ── AI Meeting Assistants ──────────────────────────────────────────────
{
  slug: 'otter',
  name: 'Otter.ai',
  category: 'AI Meeting Assistants',
  domain: 'otter.ai'
}, {
  slug: 'fireflies',
  name: 'Fireflies.ai',
  category: 'AI Meeting Assistants',
  domain: 'fireflies.ai'
}, {
  slug: 'fathom',
  name: 'Fathom',
  category: 'AI Meeting Assistants',
  domain: 'fathom.video'
}, {
  slug: 'granola',
  name: 'Granola',
  category: 'AI Meeting Assistants',
  domain: 'granola.ai'
}, {
  slug: 'fellow',
  name: 'Fellow',
  category: 'AI Meeting Assistants',
  domain: 'fellow.app'
},
// ── AI Models ──────────────────────────────────────────────────────────
{
  slug: 'openai',
  name: 'OpenAI',
  category: 'AI Models',
  domain: 'openai.com'
}, {
  slug: 'anthropic',
  name: 'Anthropic',
  category: 'AI Models',
  domain: 'anthropic.com'
}, {
  slug: 'googlegemini',
  name: 'Gemini',
  category: 'AI Models',
  domain: 'gemini.google.com'
}, {
  slug: 'mistralai',
  name: 'Mistral',
  category: 'AI Models',
  domain: 'mistral.ai'
}, {
  slug: 'perplexity',
  name: 'Perplexity',
  category: 'AI Models',
  domain: 'perplexity.ai'
}, {
  slug: 'huggingface',
  name: 'Hugging Face',
  category: 'AI Models',
  domain: 'huggingface.co'
}, {
  slug: 'cohere',
  name: 'Cohere',
  category: 'AI Models',
  domain: 'cohere.com'
}, {
  slug: 'ollama',
  name: 'Ollama',
  category: 'AI Models',
  domain: 'ollama.com'
},
// ── AI Web Scraping ────────────────────────────────────────────────────
{
  slug: 'firecrawl',
  name: 'Firecrawl',
  category: 'AI Web Scraping',
  domain: 'firecrawl.dev'
}, {
  slug: 'apify',
  name: 'Apify',
  category: 'AI Web Scraping',
  domain: 'apify.com'
}, {
  slug: 'brightdata',
  name: 'Bright Data',
  category: 'AI Web Scraping',
  domain: 'brightdata.com'
}, {
  slug: 'scrapingbee',
  name: 'ScrapingBee',
  category: 'AI Web Scraping',
  domain: 'scrapingbee.com'
},
// ── Analytics ──────────────────────────────────────────────────────────
{
  slug: 'googleanalytics',
  name: 'Google Analytics',
  category: 'Analytics',
  domain: 'analytics.google.com'
}, {
  slug: 'mixpanel',
  name: 'Mixpanel',
  category: 'Analytics',
  domain: 'mixpanel.com'
}, {
  slug: 'amplitude',
  name: 'Amplitude',
  category: 'Analytics',
  domain: 'amplitude.com'
}, {
  slug: 'segment',
  name: 'Segment',
  category: 'Analytics',
  domain: 'segment.com'
}, {
  slug: 'posthog',
  name: 'PostHog',
  category: 'Analytics',
  domain: 'posthog.com'
}, {
  slug: 'plausibleanalytics',
  name: 'Plausible',
  category: 'Analytics',
  domain: 'plausible.io'
}, {
  slug: 'looker',
  name: 'Looker',
  category: 'Analytics',
  domain: 'looker.com'
}, {
  slug: 'tableau',
  name: 'Tableau',
  category: 'Analytics',
  domain: 'tableau.com'
}, {
  slug: 'powerbi',
  name: 'Power BI',
  category: 'Analytics',
  domain: 'powerbi.microsoft.com'
}, {
  slug: 'hotjar',
  name: 'Hotjar',
  category: 'Analytics',
  domain: 'hotjar.com'
}, {
  slug: 'metabase',
  name: 'Metabase',
  category: 'Analytics',
  domain: 'metabase.com'
},
// ── Artificial Intelligence ────────────────────────────────────────────
{
  slug: 'midjourney',
  name: 'Midjourney',
  category: 'Artificial Intelligence',
  light: true
}, {
  slug: 'replicate',
  name: 'Replicate',
  category: 'Artificial Intelligence',
  light: true
}, {
  slug: 'elevenlabs',
  name: 'ElevenLabs',
  category: 'Artificial Intelligence',
  light: true
}, {
  slug: 'runway',
  name: 'Runway',
  category: 'Artificial Intelligence',
  light: true
}, {
  slug: 'stabilityai',
  name: 'Stability AI',
  category: 'Artificial Intelligence'
}, {
  slug: 'pinecone',
  name: 'Pinecone',
  category: 'Artificial Intelligence',
  light: true
},
// ── Bookmark Managers ──────────────────────────────────────────────────
{
  slug: 'raindropdotio',
  name: 'Raindrop',
  category: 'Bookmark Managers'
}, {
  slug: 'pocket',
  name: 'Pocket',
  category: 'Bookmark Managers'
}, {
  slug: 'pinboard',
  name: 'Pinboard',
  category: 'Bookmark Managers'
},
// ── Calendar ───────────────────────────────────────────────────────────
{
  slug: 'googlecalendar',
  name: 'Google Calendar',
  category: 'Calendar'
}, {
  slug: 'microsoftoutlook',
  name: 'Outlook Calendar',
  category: 'Calendar'
}, {
  slug: 'fantastical',
  name: 'Fantastical',
  category: 'Calendar'
}, {
  slug: 'notion',
  name: 'Notion Calendar',
  category: 'Calendar',
  light: true
},
// ── CMS ────────────────────────────────────────────────────────────────
{
  slug: 'wordpress',
  name: 'WordPress',
  category: 'CMS'
}, {
  slug: 'contentful',
  name: 'Contentful',
  category: 'CMS'
}, {
  slug: 'sanity',
  name: 'Sanity',
  category: 'CMS'
}, {
  slug: 'strapi',
  name: 'Strapi',
  category: 'CMS',
  light: true
}, {
  slug: 'ghost',
  name: 'Ghost',
  category: 'CMS',
  light: true
}, {
  slug: 'storyblok',
  name: 'Storyblok',
  category: 'CMS'
},
// ── Communication ──────────────────────────────────────────────────────
{
  slug: 'telegram',
  name: 'Telegram',
  category: 'Communication'
}, {
  slug: 'whatsapp',
  name: 'WhatsApp',
  category: 'Communication'
}, {
  slug: 'signal',
  name: 'Signal',
  category: 'Communication'
}, {
  slug: 'messenger',
  name: 'Messenger',
  category: 'Communication'
}, {
  slug: 'wechat',
  name: 'WeChat',
  category: 'Communication'
},
// ── Contact Management ─────────────────────────────────────────────────
{
  slug: 'google',
  name: 'Google Contacts',
  category: 'Contact Management'
}, {
  slug: 'clay',
  name: 'Clay',
  category: 'Contact Management',
  light: true
}, {
  slug: 'dex',
  name: 'Dex',
  category: 'Contact Management'
},
// ── CRM ────────────────────────────────────────────────────────────────
{
  slug: 'salesforce',
  name: 'Salesforce',
  category: 'CRM'
}, {
  slug: 'hubspot',
  name: 'HubSpot',
  category: 'CRM'
}, {
  slug: 'pipedrive',
  name: 'Pipedrive',
  category: 'CRM'
}, {
  slug: 'attio',
  name: 'Attio',
  category: 'CRM',
  light: true
}, {
  slug: 'zoho',
  name: 'Zoho CRM',
  category: 'CRM'
}, {
  slug: 'copper',
  name: 'Copper',
  category: 'CRM'
},
// ── Customer Support ───────────────────────────────────────────────────
{
  slug: 'zendesk',
  name: 'Zendesk',
  category: 'Customer Support',
  light: true
}, {
  slug: 'intercom',
  name: 'Intercom',
  category: 'Customer Support'
}, {
  slug: 'front',
  name: 'Front',
  category: 'Customer Support'
}, {
  slug: 'helpscout',
  name: 'Help Scout',
  category: 'Customer Support'
}, {
  slug: 'freshworks',
  name: 'Freshdesk',
  category: 'Customer Support'
}, {
  slug: 'gorgias',
  name: 'Gorgias',
  category: 'Customer Support'
}, {
  slug: 'crisp',
  name: 'Crisp',
  category: 'Customer Support'
},
// ── Databases ──────────────────────────────────────────────────────────
{
  slug: 'postgresql',
  name: 'PostgreSQL',
  category: 'Databases'
}, {
  slug: 'mysql',
  name: 'MySQL',
  category: 'Databases'
}, {
  slug: 'mongodb',
  name: 'MongoDB',
  category: 'Databases'
}, {
  slug: 'redis',
  name: 'Redis',
  category: 'Databases'
}, {
  slug: 'supabase',
  name: 'Supabase',
  category: 'Databases'
}, {
  slug: 'planetscale',
  name: 'PlanetScale',
  category: 'Databases',
  light: true
}, {
  slug: 'snowflake',
  name: 'Snowflake',
  category: 'Databases'
}, {
  slug: 'googlebigquery',
  name: 'BigQuery',
  category: 'Databases'
}, {
  slug: 'firebase',
  name: 'Firebase',
  category: 'Databases'
}, {
  slug: 'databricks',
  name: 'Databricks',
  category: 'Databases'
},
// ── Design ─────────────────────────────────────────────────────────────
{
  slug: 'figma',
  name: 'Figma',
  category: 'Design'
}, {
  slug: 'miro',
  name: 'Miro',
  category: 'Design'
}, {
  slug: 'sketch',
  name: 'Sketch',
  category: 'Design'
}, {
  slug: 'framer',
  name: 'Framer',
  category: 'Design',
  light: true
}, {
  slug: 'zeplin',
  name: 'Zeplin',
  category: 'Design'
},
// ── Developer Tools ────────────────────────────────────────────────────
{
  slug: 'github',
  name: 'GitHub',
  category: 'Developer Tools',
  light: true
}, {
  slug: 'gitlab',
  name: 'GitLab',
  category: 'Developer Tools'
}, {
  slug: 'bitbucket',
  name: 'Bitbucket',
  category: 'Developer Tools'
}, {
  slug: 'vercel',
  name: 'Vercel',
  category: 'Developer Tools',
  light: true
}, {
  slug: 'netlify',
  name: 'Netlify',
  category: 'Developer Tools'
}, {
  slug: 'docker',
  name: 'Docker',
  category: 'Developer Tools'
}, {
  slug: 'amazonaws',
  name: 'AWS',
  category: 'Developer Tools',
  light: true
}, {
  slug: 'googlecloud',
  name: 'Google Cloud',
  category: 'Developer Tools'
}, {
  slug: 'microsoftazure',
  name: 'Azure',
  category: 'Developer Tools'
}, {
  slug: 'sentry',
  name: 'Sentry',
  category: 'Developer Tools'
}, {
  slug: 'postman',
  name: 'Postman',
  category: 'Developer Tools'
}, {
  slug: 'circleci',
  name: 'CircleCI',
  category: 'Developer Tools',
  light: true
}, {
  slug: 'cloudflare',
  name: 'Cloudflare',
  category: 'Developer Tools'
}, {
  slug: 'heroku',
  name: 'Heroku',
  category: 'Developer Tools'
},
// ── Documents ──────────────────────────────────────────────────────────
{
  slug: 'googledocs',
  name: 'Google Docs',
  category: 'Documents',
  light: true
}, {
  slug: 'coda',
  name: 'Coda',
  category: 'Documents'
}, {
  slug: 'microsoftword',
  name: 'Word',
  category: 'Documents'
}, {
  slug: 'dropbox',
  name: 'Dropbox Paper',
  category: 'Documents'
},
// ── E-commerce ─────────────────────────────────────────────────────────
{
  slug: 'shopify',
  name: 'Shopify',
  category: 'E-commerce'
}, {
  slug: 'woocommerce',
  name: 'WooCommerce',
  category: 'E-commerce'
}, {
  slug: 'magento',
  name: 'Magento',
  category: 'E-commerce'
}, {
  slug: 'bigcommerce',
  name: 'BigCommerce',
  category: 'E-commerce',
  light: true
}, {
  slug: 'etsy',
  name: 'Etsy',
  category: 'E-commerce'
}, {
  slug: 'wix',
  name: 'Wix',
  category: 'E-commerce',
  light: true
},
// ── Education ──────────────────────────────────────────────────────────
{
  slug: 'googleclassroom',
  name: 'Google Classroom',
  category: 'Education'
}, {
  slug: 'coursera',
  name: 'Coursera',
  category: 'Education'
}, {
  slug: 'udemy',
  name: 'Udemy',
  category: 'Education'
}, {
  slug: 'duolingo',
  name: 'Duolingo',
  category: 'Education'
}, {
  slug: 'khanacademy',
  name: 'Khan Academy',
  category: 'Education'
},
// ── Email ──────────────────────────────────────────────────────────────
{
  slug: 'gmail',
  name: 'Gmail',
  category: 'Email'
}, {
  slug: 'microsoftoutlook',
  name: 'Outlook',
  category: 'Email'
}, {
  slug: 'superhuman',
  name: 'Superhuman',
  category: 'Email',
  light: true
}, {
  slug: 'protonmail',
  name: 'Proton Mail',
  category: 'Email'
},
// ── Email Newsletters ──────────────────────────────────────────────────
{
  slug: 'mailchimp',
  name: 'Mailchimp',
  category: 'Email Newsletters'
}, {
  slug: 'substack',
  name: 'Substack',
  category: 'Email Newsletters'
}, {
  slug: 'convertkit',
  name: 'ConvertKit',
  category: 'Email Newsletters'
}, {
  slug: 'beehiiv',
  name: 'beehiiv',
  category: 'Email Newsletters'
}, {
  slug: 'klaviyo',
  name: 'Klaviyo',
  category: 'Email Newsletters',
  light: true
},
// ── Event Management ───────────────────────────────────────────────────
{
  slug: 'eventbrite',
  name: 'Eventbrite',
  category: 'Event Management'
}, {
  slug: 'meetup',
  name: 'Meetup',
  category: 'Event Management'
}, {
  slug: 'lumalabs',
  name: 'Luma',
  category: 'Event Management'
},
// ── File Management & Storage ──────────────────────────────────────────
{
  slug: 'googledrive',
  name: 'Google Drive',
  category: 'File Management & Storage'
}, {
  slug: 'dropbox',
  name: 'Dropbox',
  category: 'File Management & Storage'
}, {
  slug: 'box',
  name: 'Box',
  category: 'File Management & Storage'
}, {
  slug: 'microsoftonedrive',
  name: 'OneDrive',
  category: 'File Management & Storage'
}, {
  slug: 'icloud',
  name: 'iCloud',
  category: 'File Management & Storage'
},
// ── Fitness ────────────────────────────────────────────────────────────
{
  slug: 'strava',
  name: 'Strava',
  category: 'Fitness'
}, {
  slug: 'fitbit',
  name: 'Fitbit',
  category: 'Fitness'
}, {
  slug: 'garmin',
  name: 'Garmin',
  category: 'Fitness'
}, {
  slug: 'myfitnesspal',
  name: 'MyFitnessPal',
  category: 'Fitness'
}, {
  slug: 'peloton',
  name: 'Peloton',
  category: 'Fitness'
},
// ── Forms & Surveys ────────────────────────────────────────────────────
{
  slug: 'typeform',
  name: 'Typeform',
  category: 'Forms & Surveys',
  light: true
}, {
  slug: 'googleforms',
  name: 'Google Forms',
  category: 'Forms & Surveys'
}, {
  slug: 'tally',
  name: 'Tally',
  category: 'Forms & Surveys'
}, {
  slug: 'jotform',
  name: 'Jotform',
  category: 'Forms & Surveys'
}, {
  slug: 'surveymonkey',
  name: 'SurveyMonkey',
  category: 'Forms & Surveys'
},
// ── Fundraising ────────────────────────────────────────────────────────
{
  slug: 'gofundme',
  name: 'GoFundMe',
  category: 'Fundraising'
}, {
  slug: 'kickstarter',
  name: 'Kickstarter',
  category: 'Fundraising',
  light: true
}, {
  slug: 'patreon',
  name: 'Patreon',
  category: 'Fundraising'
}, {
  slug: 'indiegogo',
  name: 'Indiegogo',
  category: 'Fundraising'
},
// ── Images & Design ────────────────────────────────────────────────────
{
  slug: 'canva',
  name: 'Canva',
  category: 'Images & Design'
}, {
  slug: 'adobe',
  name: 'Adobe CC',
  category: 'Images & Design'
}, {
  slug: 'unsplash',
  name: 'Unsplash',
  category: 'Images & Design',
  light: true
}, {
  slug: 'dribbble',
  name: 'Dribbble',
  category: 'Images & Design'
}, {
  slug: 'behance',
  name: 'Behance',
  category: 'Images & Design'
},
// ── IT Operations ──────────────────────────────────────────────────────
{
  slug: 'pagerduty',
  name: 'PagerDuty',
  category: 'IT Operations'
}, {
  slug: 'opsgenie',
  name: 'Opsgenie',
  category: 'IT Operations'
}, {
  slug: 'servicenow',
  name: 'ServiceNow',
  category: 'IT Operations'
}, {
  slug: 'okta',
  name: 'Okta',
  category: 'IT Operations'
},
// ── Lifestyle & Entertainment ──────────────────────────────────────────
{
  slug: 'spotify',
  name: 'Spotify',
  category: 'Lifestyle & Entertainment'
}, {
  slug: 'netflix',
  name: 'Netflix',
  category: 'Lifestyle & Entertainment'
}, {
  slug: 'youtube',
  name: 'YouTube',
  category: 'Lifestyle & Entertainment'
}, {
  slug: 'applemusic',
  name: 'Apple Music',
  category: 'Lifestyle & Entertainment'
}, {
  slug: 'goodreads',
  name: 'Goodreads',
  category: 'Lifestyle & Entertainment'
},
// ── Marketing ──────────────────────────────────────────────────────────
{
  slug: 'semrush',
  name: 'Semrush',
  category: 'Marketing'
}, {
  slug: 'ahrefs',
  name: 'Ahrefs',
  category: 'Marketing'
}, {
  slug: 'googlesearchconsole',
  name: 'Search Console',
  category: 'Marketing'
}, {
  slug: 'marketo',
  name: 'Marketo',
  category: 'Marketing'
},
// ── Marketing Automation ───────────────────────────────────────────────
{
  slug: 'customerio',
  name: 'Customer.io',
  category: 'Marketing Automation'
}, {
  slug: 'activecampaign',
  name: 'ActiveCampaign',
  category: 'Marketing Automation'
}, {
  slug: 'braze',
  name: 'Braze',
  category: 'Marketing Automation'
}, {
  slug: 'sendgrid',
  name: 'SendGrid',
  category: 'Marketing Automation'
}, {
  slug: 'iterable',
  name: 'Iterable',
  category: 'Marketing Automation'
},
// ── Notes ──────────────────────────────────────────────────────────────
{
  slug: 'notion',
  name: 'Notion',
  category: 'Notes',
  light: true
}, {
  slug: 'evernote',
  name: 'Evernote',
  category: 'Notes'
}, {
  slug: 'obsidian',
  name: 'Obsidian',
  category: 'Notes'
}, {
  slug: 'microsoftonenote',
  name: 'OneNote',
  category: 'Notes'
}, {
  slug: 'bear',
  name: 'Bear',
  category: 'Notes'
},
// ── Notifications ──────────────────────────────────────────────────────
{
  slug: 'pushover',
  name: 'Pushover',
  category: 'Notifications'
}, {
  slug: 'pushbullet',
  name: 'Pushbullet',
  category: 'Notifications'
}, {
  slug: 'ntfy',
  name: 'ntfy',
  category: 'Notifications'
},
// ── Payment Processing ─────────────────────────────────────────────────
{
  slug: 'stripe',
  name: 'Stripe',
  category: 'Payment Processing'
}, {
  slug: 'paypal',
  name: 'PayPal',
  category: 'Payment Processing'
}, {
  slug: 'square',
  name: 'Square',
  category: 'Payment Processing',
  light: true
}, {
  slug: 'adyen',
  name: 'Adyen',
  category: 'Payment Processing'
}, {
  slug: 'razorpay',
  name: 'Razorpay',
  category: 'Payment Processing'
}, {
  slug: 'wise',
  name: 'Wise',
  category: 'Payment Processing'
},
// ── Phone & SMS ────────────────────────────────────────────────────────
{
  slug: 'twilio',
  name: 'Twilio',
  category: 'Phone & SMS'
}, {
  slug: 'messagebird',
  name: 'MessageBird',
  category: 'Phone & SMS'
}, {
  slug: 'vonage',
  name: 'Vonage',
  category: 'Phone & SMS'
}, {
  slug: 'plivo',
  name: 'Plivo',
  category: 'Phone & SMS'
},
// ── Product Management ─────────────────────────────────────────────────
{
  slug: 'productboard',
  name: 'Productboard',
  category: 'Product Management'
}, {
  slug: 'pendo',
  name: 'Pendo',
  category: 'Product Management'
}, {
  slug: 'canny',
  name: 'Canny',
  category: 'Product Management'
},
// ── Productivity ───────────────────────────────────────────────────────
{
  slug: 'zapier',
  name: 'Zapier',
  category: 'Productivity'
}, {
  slug: 'make',
  name: 'Make',
  category: 'Productivity',
  light: true
}, {
  slug: 'n8n',
  name: 'n8n',
  category: 'Productivity'
}, {
  slug: 'ifttt',
  name: 'IFTTT',
  category: 'Productivity'
},
// ── Project Management ─────────────────────────────────────────────────
{
  slug: 'linear',
  name: 'Linear',
  category: 'Project Management'
}, {
  slug: 'jira',
  name: 'Jira',
  category: 'Project Management'
}, {
  slug: 'asana',
  name: 'Asana',
  category: 'Project Management'
}, {
  slug: 'mondaydotcom',
  name: 'Monday.com',
  category: 'Project Management'
}, {
  slug: 'clickup',
  name: 'ClickUp',
  category: 'Project Management'
}, {
  slug: 'basecamp',
  name: 'Basecamp',
  category: 'Project Management',
  light: true
}, {
  slug: 'wrike',
  name: 'Wrike',
  category: 'Project Management'
},
// ── Proposal & Invoice Management ──────────────────────────────────────
{
  slug: 'pandadoc',
  name: 'PandaDoc',
  category: 'Proposal & Invoice Management'
}, {
  slug: 'proposify',
  name: 'Proposify',
  category: 'Proposal & Invoice Management'
}, {
  slug: 'qonto',
  name: 'Qonto',
  category: 'Proposal & Invoice Management'
},
// ── Sales ──────────────────────────────────────────────────────────────
{
  slug: 'gong',
  name: 'Gong',
  category: 'Sales'
}, {
  slug: 'outreach',
  name: 'Outreach',
  category: 'Sales'
}, {
  slug: 'salesloft',
  name: 'Salesloft',
  category: 'Sales'
}, {
  slug: 'apollo',
  name: 'Apollo.io',
  category: 'Sales'
},
// ── Scheduling & Booking ───────────────────────────────────────────────
{
  slug: 'calendly',
  name: 'Calendly',
  category: 'Scheduling & Booking'
}, {
  slug: 'caldotcom',
  name: 'Cal.com',
  category: 'Scheduling & Booking',
  light: true
}, {
  slug: 'doodle',
  name: 'Doodle',
  category: 'Scheduling & Booking'
}, {
  slug: 'acuityscheduling',
  name: 'Acuity',
  category: 'Scheduling & Booking'
},
// ── Security ───────────────────────────────────────────────────────────
{
  slug: 'okta',
  name: 'Okta',
  category: 'Security'
}, {
  slug: '1password',
  name: '1Password',
  category: 'Security',
  light: true
}, {
  slug: 'lastpass',
  name: 'LastPass',
  category: 'Security'
}, {
  slug: 'bitwarden',
  name: 'Bitwarden',
  category: 'Security'
}, {
  slug: 'auth0',
  name: 'Auth0',
  category: 'Security',
  light: true
},
// ── Server Monitoring ──────────────────────────────────────────────────
{
  slug: 'datadog',
  name: 'Datadog',
  category: 'Server Monitoring'
}, {
  slug: 'newrelic',
  name: 'New Relic',
  category: 'Server Monitoring'
}, {
  slug: 'grafana',
  name: 'Grafana',
  category: 'Server Monitoring'
}, {
  slug: 'prometheus',
  name: 'Prometheus',
  category: 'Server Monitoring'
},
// ── Signatures ─────────────────────────────────────────────────────────
{
  slug: 'docusign',
  name: 'DocuSign',
  category: 'Signatures'
}, {
  slug: 'hellosign',
  name: 'Dropbox Sign',
  category: 'Signatures'
}, {
  slug: 'adobe',
  name: 'Adobe Sign',
  category: 'Signatures'
},
// ── Social Media Accounts ──────────────────────────────────────────────
{
  slug: 'x',
  name: 'X (Twitter)',
  category: 'Social Media Accounts',
  light: true
}, {
  slug: 'instagram',
  name: 'Instagram',
  category: 'Social Media Accounts'
}, {
  slug: 'youtube',
  name: 'YouTube',
  category: 'Social Media Accounts'
}, {
  slug: 'linkedin',
  name: 'LinkedIn',
  category: 'Social Media Accounts'
}, {
  slug: 'facebook',
  name: 'Facebook',
  category: 'Social Media Accounts'
}, {
  slug: 'tiktok',
  name: 'TikTok',
  category: 'Social Media Accounts',
  light: true
}, {
  slug: 'threads',
  name: 'Threads',
  category: 'Social Media Accounts',
  light: true
}, {
  slug: 'reddit',
  name: 'Reddit',
  category: 'Social Media Accounts'
},
// ── Social Media Marketing ─────────────────────────────────────────────
{
  slug: 'buffer',
  name: 'Buffer',
  category: 'Social Media Marketing',
  light: true
}, {
  slug: 'hootsuite',
  name: 'Hootsuite',
  category: 'Social Media Marketing'
}, {
  slug: 'later',
  name: 'Later',
  category: 'Social Media Marketing'
}, {
  slug: 'sproutsocial',
  name: 'Sprout Social',
  category: 'Social Media Marketing'
},
// ── Spreadsheets ───────────────────────────────────────────────────────
{
  slug: 'googlesheets',
  name: 'Google Sheets',
  category: 'Spreadsheets'
}, {
  slug: 'microsoftexcel',
  name: 'Excel',
  category: 'Spreadsheets'
}, {
  slug: 'airtable',
  name: 'Airtable',
  category: 'Spreadsheets'
}, {
  slug: 'smartsheet',
  name: 'Smartsheet',
  category: 'Spreadsheets'
},
// ── Task Management ────────────────────────────────────────────────────
{
  slug: 'trello',
  name: 'Trello',
  category: 'Task Management'
}, {
  slug: 'todoist',
  name: 'Todoist',
  category: 'Task Management'
}, {
  slug: 'ticktick',
  name: 'TickTick',
  category: 'Task Management'
}, {
  slug: 'things',
  name: 'Things',
  category: 'Task Management'
},
// ── Team Chat ──────────────────────────────────────────────────────────
{
  slug: 'slack',
  name: 'Slack',
  category: 'Team Chat'
}, {
  slug: 'discord',
  name: 'Discord',
  category: 'Team Chat'
}, {
  slug: 'microsoftteams',
  name: 'Microsoft Teams',
  category: 'Team Chat'
}, {
  slug: 'googlechat',
  name: 'Google Chat',
  category: 'Team Chat'
}, {
  slug: 'mattermost',
  name: 'Mattermost',
  category: 'Team Chat'
},
// ── Team Collaboration ─────────────────────────────────────────────────
{
  slug: 'atlassian',
  name: 'Atlassian',
  category: 'Team Collaboration'
}, {
  slug: 'confluence',
  name: 'Confluence',
  category: 'Team Collaboration',
  light: true
},
// ── Time Tracking Software ─────────────────────────────────────────────
{
  slug: 'toggltrack',
  name: 'Toggl',
  category: 'Time Tracking Software'
}, {
  slug: 'harvest',
  name: 'Harvest',
  category: 'Time Tracking Software'
}, {
  slug: 'clockify',
  name: 'Clockify',
  category: 'Time Tracking Software'
}, {
  slug: 'rescuetime',
  name: 'RescueTime',
  category: 'Time Tracking Software'
},
// ── Transcription ──────────────────────────────────────────────────────
{
  slug: 'descript',
  name: 'Descript',
  category: 'Transcription',
  light: true
}, {
  slug: 'rev',
  name: 'Rev',
  category: 'Transcription'
}, {
  slug: 'trint',
  name: 'Trint',
  category: 'Transcription'
},
// ── URL Shortener ──────────────────────────────────────────────────────
{
  slug: 'bitly',
  name: 'Bitly',
  category: 'URL Shortener'
}, {
  slug: 'dub',
  name: 'Dub',
  category: 'URL Shortener',
  light: true
}, {
  slug: 'rebrandly',
  name: 'Rebrandly',
  category: 'URL Shortener'
},
// ── Video & Audio ──────────────────────────────────────────────────────
{
  slug: 'loom',
  name: 'Loom',
  category: 'Video & Audio'
}, {
  slug: 'vimeo',
  name: 'Vimeo',
  category: 'Video & Audio'
}, {
  slug: 'riverside',
  name: 'Riverside',
  category: 'Video & Audio'
},
// ── Video Conferencing ─────────────────────────────────────────────────
{
  slug: 'zoom',
  name: 'Zoom',
  category: 'Video Conferencing'
}, {
  slug: 'googlemeet',
  name: 'Google Meet',
  category: 'Video Conferencing'
}, {
  slug: 'webex',
  name: 'Webex',
  category: 'Video Conferencing'
},
// ── Website Builders ───────────────────────────────────────────────────
{
  slug: 'webflow',
  name: 'Webflow',
  category: 'Website Builders',
  light: true
}, {
  slug: 'framer',
  name: 'Framer',
  category: 'Website Builders',
  light: true
}, {
  slug: 'squarespace',
  name: 'Squarespace',
  category: 'Website Builders',
  light: true
}, {
  slug: 'wix',
  name: 'Wix',
  category: 'Website Builders',
  light: true
}, {
  slug: 'carrd',
  name: 'Carrd',
  category: 'Website Builders'
},
// ── HR & Recruiting ──────────────────────────────────────────────────────
{
  slug: 'workday',
  name: 'Workday',
  category: 'HR & Recruiting'
}, {
  slug: 'bamboohr',
  name: 'BambooHR',
  category: 'HR & Recruiting'
}, {
  slug: 'gusto',
  name: 'Gusto',
  category: 'HR & Recruiting'
}, {
  slug: 'rippling',
  name: 'Rippling',
  category: 'HR & Recruiting',
  light: true
}, {
  slug: 'deel',
  name: 'Deel',
  category: 'HR & Recruiting'
}, {
  slug: 'greenhouse',
  name: 'Greenhouse',
  category: 'HR & Recruiting'
}, {
  slug: 'lever',
  name: 'Lever',
  category: 'HR & Recruiting'
}, {
  slug: 'adp',
  name: 'ADP',
  category: 'HR & Recruiting'
},
// ── Legal & Contracts ────────────────────────────────────────────────────
{
  slug: 'clio',
  name: 'Clio',
  category: 'Legal & Contracts'
}, {
  slug: 'ironcladuniverse',
  name: 'Ironclad',
  category: 'Legal & Contracts'
}, {
  slug: 'pandadoc',
  name: 'PandaDoc',
  category: 'Legal & Contracts'
}, {
  slug: 'lexisnexis',
  name: 'LexisNexis',
  category: 'Legal & Contracts'
}, {
  slug: 'docusign',
  name: 'DocuSign',
  category: 'Legal & Contracts'
},
// ── Healthcare ───────────────────────────────────────────────────────────
{
  slug: 'epicgames',
  name: 'Epic',
  category: 'Healthcare',
  light: true
}, {
  slug: 'athenahealth',
  name: 'athenahealth',
  category: 'Healthcare'
}, {
  slug: 'zocdoc',
  name: 'Zocdoc',
  category: 'Healthcare'
}, {
  slug: 'doximity',
  name: 'Doximity',
  category: 'Healthcare'
}, {
  slug: 'veeva',
  name: 'Veeva',
  category: 'Healthcare'
},
// ── Real Estate ──────────────────────────────────────────────────────────
{
  slug: 'zillow',
  name: 'Zillow',
  category: 'Real Estate'
}, {
  slug: 'redfin',
  name: 'Redfin',
  category: 'Real Estate'
}, {
  slug: 'opendoor',
  name: 'Opendoor',
  category: 'Real Estate'
}, {
  slug: 'compass',
  name: 'Compass',
  category: 'Real Estate'
},
// ── Logistics & Shipping ─────────────────────────────────────────────────
{
  slug: 'fedex',
  name: 'FedEx',
  category: 'Logistics & Shipping'
}, {
  slug: 'ups',
  name: 'UPS',
  category: 'Logistics & Shipping'
}, {
  slug: 'dhl',
  name: 'DHL',
  category: 'Logistics & Shipping'
}, {
  slug: 'shippo',
  name: 'Shippo',
  category: 'Logistics & Shipping'
}, {
  slug: 'easypost',
  name: 'EasyPost',
  category: 'Logistics & Shipping'
}, {
  slug: 'shipstation',
  name: 'ShipStation',
  category: 'Logistics & Shipping'
},
// ── Cloud Infrastructure ─────────────────────────────────────────────────
{
  slug: 'amazonaws',
  name: 'AWS',
  category: 'Cloud Infrastructure',
  light: true
}, {
  slug: 'googlecloud',
  name: 'Google Cloud',
  category: 'Cloud Infrastructure'
}, {
  slug: 'microsoftazure',
  name: 'Azure',
  category: 'Cloud Infrastructure'
}, {
  slug: 'digitalocean',
  name: 'DigitalOcean',
  category: 'Cloud Infrastructure'
}, {
  slug: 'vercel',
  name: 'Vercel',
  category: 'Cloud Infrastructure',
  light: true
}, {
  slug: 'netlify',
  name: 'Netlify',
  category: 'Cloud Infrastructure'
}, {
  slug: 'heroku',
  name: 'Heroku',
  category: 'Cloud Infrastructure'
}, {
  slug: 'render',
  name: 'Render',
  category: 'Cloud Infrastructure'
}, {
  slug: 'cloudflare',
  name: 'Cloudflare',
  category: 'Cloud Infrastructure'
},
// ── CI/CD & DevOps ───────────────────────────────────────────────────────
{
  slug: 'githubactions',
  name: 'GitHub Actions',
  category: 'CI/CD & DevOps',
  light: true
}, {
  slug: 'circleci',
  name: 'CircleCI',
  category: 'CI/CD & DevOps',
  light: true
}, {
  slug: 'jenkins',
  name: 'Jenkins',
  category: 'CI/CD & DevOps'
}, {
  slug: 'gitlab',
  name: 'GitLab',
  category: 'CI/CD & DevOps'
}, {
  slug: 'argo',
  name: 'Argo CD',
  category: 'CI/CD & DevOps'
}, {
  slug: 'terraform',
  name: 'Terraform',
  category: 'CI/CD & DevOps'
},
// ── Observability & Monitoring ───────────────────────────────────────────
{
  slug: 'datadog',
  name: 'Datadog',
  category: 'Observability & Monitoring'
}, {
  slug: 'grafana',
  name: 'Grafana',
  category: 'Observability & Monitoring'
}, {
  slug: 'newrelic',
  name: 'New Relic',
  category: 'Observability & Monitoring'
}, {
  slug: 'sentry',
  name: 'Sentry',
  category: 'Observability & Monitoring'
}, {
  slug: 'prometheus',
  name: 'Prometheus',
  category: 'Observability & Monitoring'
}, {
  slug: 'pagerduty',
  name: 'PagerDuty',
  category: 'Observability & Monitoring'
}, {
  slug: 'splunk',
  name: 'Splunk',
  category: 'Observability & Monitoring'
},
// ── Data Warehouses ──────────────────────────────────────────────────────
{
  slug: 'snowflake',
  name: 'Snowflake',
  category: 'Data Warehouses'
}, {
  slug: 'googlebigquery',
  name: 'BigQuery',
  category: 'Data Warehouses'
}, {
  slug: 'databricks',
  name: 'Databricks',
  category: 'Data Warehouses'
}, {
  slug: 'amazonredshift',
  name: 'Redshift',
  category: 'Data Warehouses'
}, {
  slug: 'clickhouse',
  name: 'ClickHouse',
  category: 'Data Warehouses',
  light: true
},
// ── ETL & Data Pipelines ─────────────────────────────────────────────────
{
  slug: 'fivetran',
  name: 'Fivetran',
  category: 'ETL & Data Pipelines'
}, {
  slug: 'airbyte',
  name: 'Airbyte',
  category: 'ETL & Data Pipelines'
}, {
  slug: 'dbt',
  name: 'dbt',
  category: 'ETL & Data Pipelines'
}, {
  slug: 'apachekafka',
  name: 'Kafka',
  category: 'ETL & Data Pipelines',
  light: true
},
// ── Knowledge Base ───────────────────────────────────────────────────────
{
  slug: 'confluence',
  name: 'Confluence',
  category: 'Knowledge Base'
}, {
  slug: 'gitbook',
  name: 'GitBook',
  category: 'Knowledge Base'
}, {
  slug: 'guru',
  name: 'Guru',
  category: 'Knowledge Base'
}, {
  slug: 'slab',
  name: 'Slab',
  category: 'Knowledge Base'
}, {
  slug: 'document360',
  name: 'Document360',
  category: 'Knowledge Base'
},
// ── Password & Identity ──────────────────────────────────────────────────
{
  slug: 'okta',
  name: 'Okta',
  category: 'Password & Identity'
}, {
  slug: '1password',
  name: '1Password',
  category: 'Password & Identity'
}, {
  slug: 'bitwarden',
  name: 'Bitwarden',
  category: 'Password & Identity'
}, {
  slug: 'lastpass',
  name: 'LastPass',
  category: 'Password & Identity'
}, {
  slug: 'auth0',
  name: 'Auth0',
  category: 'Password & Identity',
  light: true
}, {
  slug: 'onelogin',
  name: 'OneLogin',
  category: 'Password & Identity'
}];
const CATEGORIES: Category[] = ['All Integrations', 'Accounting', 'Ads & Conversion', 'AI Agents', 'AI Meeting Assistants', 'AI Models', 'AI Web Scraping', 'Analytics', 'Artificial Intelligence', 'Bookmark Managers', 'Calendar', 'CMS', 'Communication', 'Contact Management', 'CRM', 'Customer Support', 'Databases', 'Design', 'Developer Tools', 'Documents', 'E-commerce', 'Education', 'Email', 'Email Newsletters', 'Event Management', 'File Management & Storage', 'Fitness', 'Forms & Surveys', 'Fundraising', 'Images & Design', 'IT Operations', 'Lifestyle & Entertainment', 'Marketing', 'Marketing Automation', 'Notes', 'Notifications', 'Payment Processing', 'Phone & SMS', 'Product Management', 'Productivity', 'Project Management', 'Proposal & Invoice Management', 'Sales', 'Scheduling & Booking', 'Security', 'Server Monitoring', 'Signatures', 'Social Media Accounts', 'Social Media Marketing', 'Spreadsheets', 'Task Management', 'Team Chat', 'Team Collaboration', 'Time Tracking Software', 'Transcription', 'URL Shortener', 'Video & Audio', 'Video Conferencing', 'Website Builders', 'CI/CD & DevOps', 'Cloud Infrastructure', 'Data Warehouses', 'ETL & Data Pipelines', 'Healthcare', 'HR & Recruiting', 'Knowledge Base', 'Legal & Contracts', 'Logistics & Shipping', 'Observability & Monitoring', 'Password & Identity', 'Real Estate'];
// Resilient logo for a directory tile. Build an ordered list of candidate
// sources and walk through them on each load error, hiding the image only if
// every source fails. When a `domain` is present we try a domain-based logo
// service FIRST — it covers virtually every real company, including newer
// brands (Ramp, Brex, CrewAI, Fireflies, Granola, Firecrawl, Bright Data, …)
// that Simple Icons / dashboard-icons don't carry. Otherwise we fall back to
// the icon CDNs as before.
function IntegrationLogo({
  integration
}: {
  integration: Integration;
}) {
  const sources = useMemo(() => {
    const list: string[] = [];
    if (integration.domain) {
      list.push(`https://logo.clearbit.com/${integration.domain}`);
    }
    list.push(`https://cdn.simpleicons.org/${integration.slug}${integration.light ? '/ffffff' : ''}`);
    list.push(`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${integration.slug}.svg`);
    // Domain favicon as a final visual safety net so a tile is never blank.
    if (integration.domain) {
      list.push(`https://www.google.com/s2/favicons?domain=${integration.domain}&sz=128`);
    }
    return list;
  }, [integration.slug, integration.light, integration.domain]);
  const [index, setIndex] = useState(0);
  return <img src={sources[index]} alt={integration.name} className="w-7 h-7 md:w-8 md:h-8 object-contain opacity-90 group-hover:opacity-100 transition-opacity" loading="lazy" onError={e => {
    if (index < sources.length - 1) {
      setIndex(i => i + 1);
    } else {
      ;
      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
    }
  }} data-id="element-6133" />;
}
export function Integrations() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category>('All Integrations');
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const filteredIntegrations = useMemo(() => {
    return INTEGRATIONS.filter(integration => {
      const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All Integrations' || integration.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory]);
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      'All Integrations': INTEGRATIONS.length
    };
    CATEGORIES.forEach(cat => {
      if (cat !== 'All Integrations') {
        counts[cat] = INTEGRATIONS.filter(i => i.category === cat).length;
      }
    });
    return counts;
  }, []);
  return <div className="min-h-screen bg-[#1A1410] text-[#FAF8F4] pt-24 md:pt-32 pb-24 font-sans" data-id="element-6134">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10" data-id="element-6135">
        {/* Header Section */}
        <div className="flex flex-col items-center text-center mb-16 md:mb-24" data-id="element-6136">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-6137">
            Integrations
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-title font-medium tracking-tight leading-[1.05] mb-6" data-id="element-6138">
            Everything you use.
            <br data-id="element-6139" />
            <span className="text-[#EA580C]" data-id="element-6140">One AI brain.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl leading-relaxed" data-id="element-6141">
            Connect your entire stack and Giga works across all of it — pulling
            context and taking action everywhere you already work.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-16 md:mb-24" data-id="element-6142">
          <div className="relative group" data-id="element-6143">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-[#EA580C] transition-colors" size={20} data-id="element-6144" />
            <input type="text" placeholder="Search integrations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#EA580C]/50 focus:border-[#EA580C]/50 transition-all" data-id="element-6145" />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-10 lg:gap-16 items-start" data-id="element-6146">
          {/* Sidebar */}
          <div className="w-full md:w-72 shrink-0 md:sticky md:top-28" data-id="element-6147">
            <h3 className="text-xs font-bold tracking-widest uppercase text-white/40 mb-6 px-4" data-id="element-6148">
              Categories
            </h3>
            <div className="flex flex-col gap-1 max-h-[420px] md:max-h-[70vh] overflow-y-auto pr-2 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.15)_transparent]" data-id="element-6149">
              {CATEGORIES.map(category => {
              const count = categoryCounts[category];
              // Only show categories that have at least one integration, plus the "All" tab
              if (count === 0 && category !== 'All Integrations') return null;
              const isActive = activeCategory === category;
              return <button key={category} onClick={() => setActiveCategory(category)} className={`flex items-center justify-between px-4 py-2.5 rounded-lg text-sm transition-all duration-200 text-left ${isActive ? 'bg-[#EA580C]/10 text-[#EA580C] font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`} data-id="element-6150">
                    <span className="truncate pr-4" data-id="element-6151">{category}</span>
                    <span className={`text-xs shrink-0 ${isActive ? 'text-[#EA580C]/80' : 'text-white/30'}`} data-id="element-6152">
                      {count}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1" data-id="element-6153">
            <div className="mb-6 text-sm text-white/40" data-id="element-6154">
              Showing {filteredIntegrations.length} integration
              {filteredIntegrations.length !== 1 ? 's' : ''}
            </div>

            {filteredIntegrations.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-6155">
                {filteredIntegrations.map((integration, idx) => <div key={`${integration.slug}-${integration.category}-${idx}`} className="group flex flex-col items-center p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] hover:border-white/10 transition-all duration-300 cursor-pointer" data-id="element-6156">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 overflow-hidden" data-id="element-6157">
                      <IntegrationLogo integration={integration} data-id="element-6158" />
                    </div>
                    <h4 className="text-base font-semibold text-white/90 mb-2 text-center" data-id="element-6159">
                      {integration.name}
                    </h4>
                    <span className="text-[10px] font-medium tracking-wide uppercase text-white/40 bg-white/5 px-2.5 py-1 rounded-md text-center" data-id="element-6160">
                      {integration.category}
                    </span>
                  </div>)}
              </div> : <div className="flex flex-col items-center justify-center py-20 text-center border border-white/5 rounded-2xl bg-white/[0.02]" data-id="element-6161">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4" data-id="element-6162">
                  <Search className="text-white/30" size={24} data-id="element-6163" />
                </div>
                <h3 className="text-xl font-medium text-white/90 mb-2" data-id="element-6164">
                  No integrations found
                </h3>
                <p className="text-white/50 max-w-sm" data-id="element-6165">
                  We couldn't find any integrations matching "{searchQuery}" in{' '}
                  {activeCategory}.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setActiveCategory('All Integrations');
            }} className="mt-6 px-5 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm font-medium transition-colors" data-id="element-6166">
                  Clear filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}